// session_recovery — PURE. Un rafraîchissement qui échoue tue-t-il la session, ou pas ?
//
// ── LE DÉFAUT QUE CE MODULE RETIRE ──────────────────────────────────────────
//
// `refreshAccessToken` rendait `string | null` : UNE seule façon d'échouer pour deux situations
// qui n'ont rien à voir. Le serveur qui REFUSE (jeton périmé, révoqué, rejoué) veut dire « la
// session est morte » ; ne pas POUVOIR demander (réseau coupé, service qui redémarre, réponse
// bloquée par le navigateur) ne veut rien dire du tout sur la session.
//
// Son `catch` prenait explicitement soin de ne pas effacer les jetons — « might be transient ».
// Mais l'appelant voyait le 401 initial toujours debout et appelait `logout()`, qui les efface :
// la précaution de l'appelé était annulée par l'appelant. Une coupure d'une seconde détruisait
// donc une session de trente jours, et renvoyait la personne à la connexion Google.
//
// C'est ce qui a rendu le défaut CORS du 2026-09-29 catastrophique au lieu d'ennuyeux : la cause
// première était la liste d'origines (lib/cors_origins.ts), mais c'est CE chemin qui transformait
// une requête à rejouer en déconnexion définitive.
//
// ── POURQUOI ICI ────────────────────────────────────────────────────────────
//
// Trois sites d'appel prennent cette décision (api.ts : `call`, `postRunBody`, le flux
// d'évènements). Recopiée trois fois, elle se corrige à moitié. Et `auth.ts` n'est pas éprouvable
// — il touche `localStorage` et `location`. La règle vit donc ici, pure, comme `preview_url.ts`.

/** Pourquoi un rafraîchissement n'a pas rendu de jeton.
 *
 *  Trois causes, parce qu'elles se réparent différemment : se reconnecter, attendre le réseau,
 *  ou se connecter tout court. */
export type RefreshFailure = "refused" | "unreachable" | "no-token";

/** Les trois cas, énumérés — pour qu'un test puisse exiger un verdict sur CHACUN. */
export const REFRESH_FAILURES: readonly RefreshFailure[] = Object.freeze(["refused", "unreachable", "no-token"]);

/**
 * PURE. Faut-il effacer la session locale ?
 *
 * Seul un REFUS du serveur le justifie : lui seul a jugé le jeton. Une panne de transport ne
 * prouve rien, et effacer sur rien coûte une reconnexion complète à la personne.
 *
 * Écrit en énumérant le cas à ÉPARGNER plutôt que les cas à tuer : un quatrième mode d'échec
 * ajouté demain sera traité comme terminal par défaut — ce qui est le côté prudent, puisqu'il
 * renvoie vers la connexion au lieu de garder une session peut-être morte.
 */
export function sessionLost(failure: RefreshFailure): boolean {
  return failure !== "unreachable";
}

/**
 * PURE. Ce que la personne lit.
 *
 * Dire « session expirée » sur un réseau coupé est deux fois faux : ça l'envoie se reconnecter
 * pour rien, et ça lui fait croire que le produit l'a éjectée. Chaque cause a donc sa phrase.
 */
export function refreshFailureMessage(failure: RefreshFailure): string {
  switch (failure) {
    case "unreachable":
      return "connexion réseau perdue — rien n'a été perdu, réessayez dans un instant";
    case "refused":
      return "votre session a expiré — veuillez vous reconnecter";
    case "no-token":
      return "vous n'êtes pas connecté — veuillez vous connecter";
  }
}
